import { FC } from "react";
import { Badge, Box, Tab, Tabs, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useOnboardingRequests } from "./onboardingRequests/queries";

import { useBrokerOnboardingRequests } from "./brokerOnboardingRequests/queries";
import { useCountryOnboardingRequests } from "./countryOnboardingRequests/queries";
import { useIsAdmin } from "../../auth/useIsAdmin";
import { AdminRoutes } from "./routes";

type AdminGroup = "counterparties" | "brokers" | "mergers" | "countries";
type AdminSubTab = "list" | "trader-access" | "onboarding-requests";

const groupFromPath = (pathname: string): AdminGroup => {
  if (pathname.includes("/admin/brokers")) return "brokers";
  if (pathname.includes("/admin/mergers")) return "mergers";
  if (pathname.includes("/admin/countries")) return "countries";
  return "counterparties";
};

// The books carry the same three sub-tabs. Countries were the odd one out until the country
// trader grant endpoints existed; they now share the counterparty and broker surface. Mergers
// only have List: they are never requested, and their trader grants live in the Counterparties
// and Brokers groups (a merger is one or the other).
const subTabFromPath = (pathname: string): AdminSubTab => {
  if (pathname.includes("/trader-access")) return "trader-access";
  if (pathname.includes("/onboarding-requests")) return "onboarding-requests";
  return "list";
};

export const Admin: FC = () => {
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: pendingRequests } = useOnboardingRequests("Pending", {
    refetchInterval: 60000
  });
  const pendingCount = pendingRequests?.length ?? 0;

  const { data: pendingBrokerRequests } = useBrokerOnboardingRequests(
    "Pending",
    { refetchInterval: 60000 }
  );
  const pendingBrokerCount = pendingBrokerRequests?.length ?? 0;

  const { data: pendingCountryRequests } = useCountryOnboardingRequests(
    "Pending",
    { refetchInterval: 60000 }
  );
  const pendingCountryCount = pendingCountryRequests?.length ?? 0;

  if (!isAdmin) {
    return (
      <Box sx={{ padding: 4 }}>
        <Typography variant="h6" gutterBottom>
          Unauthorized to view this page
        </Typography>
      </Box>
    );
  }

  const group = groupFromPath(location.pathname);
  const subTab = subTabFromPath(location.pathname);
  const onboardingBadge =
    group === "brokers"
      ? pendingBrokerCount
      : group === "countries"
        ? pendingCountryCount
        : pendingCount;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        padding: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2
      }}
    >
      <Tabs
        value={group}
        onChange={(_e, next: AdminGroup) => navigate(`/admin/${next}/list`)}
        textColor="secondary"
        indicatorColor="secondary"
      >
        <Tab value="counterparties" label="Counterparties" />
        <Tab
          value="brokers"
          sx={{ paddingRight: 3 }}
          label={
            <Badge
              badgeContent={pendingBrokerCount}
              color="warning"
              sx={{ "& .MuiBadge-badge": { right: -10, top: 0 } }}
            >
              Brokers
            </Badge>
          }
        />
        <Tab value="mergers" label="Mergers" />
        <Tab
          value="countries"
          sx={{ paddingRight: 3 }}
          label={
            <Badge
              badgeContent={pendingCountryCount}
              color="warning"
              sx={{ "& .MuiBadge-badge": { right: -10, top: 0 } }}
            >
              Country additions
            </Badge>
          }
        />
      </Tabs>

      <Tabs
        value={subTab}
        onChange={(_e, next: AdminSubTab) =>
          navigate(`/admin/${group}/${next}`)
        }
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab value="list" label="List" />
        {group !== "mergers" && (
          <Tab value="trader-access" label="Trader access" />
        )}
        {group !== "mergers" && (
          <Tab
            value="onboarding-requests"
            sx={{ paddingRight: 3 }}
            label={
              <Badge
                badgeContent={onboardingBadge}
                color="warning"
                sx={{ "& .MuiBadge-badge": { right: -10, top: 0 } }}
              >
                Onboarding requests
              </Badge>
            }
          />
        )}
      </Tabs>

      <AdminRoutes />
    </Box>
  );
};
